// Keyboard hotkey routing for the game board, extracted from
// GameBoard.jsx. handleGameHotkey resolves a keydown against the
// user's keybinding table (spectators pass through a read-only
// allow-list); dispatchHotkeyAction maps an action id onto the host
// primitive that performs it. The flip/tap hotkeys act on the hovered
// card or the marquee selection.
//
// Host contract: state, props, scene, meshes, hoveredMesh,
// selectedCardIds, plus the action primitives (flipCard, _applyTap,
// drawCard, passTurn, fan/pile/menu open-close methods).

import { animateCardFlip, animateShufflePile } from '../../../utils/game/animations';
import { nextTapToggle } from '../../../utils/game/engine/core';
import { syncCardFace } from '../../../utils/game/engine/core/cardFace';
import { shufflePile } from '../../../utils/game/gameState';
import { logShuffle } from './cardPileLifecycle';
import { matchAction } from '../../../utils/game/keybindings';
import { DICE_CONFIGS } from '../../../utils/game/diceMesh';
import { boardBlockingPrompt, serverChoiceDisplayCard } from '../EffectPromptDrawer';
import { playSound } from '../../../utils/game/sounds';
import { toast } from 'sonner';
import { cardLogLabel, isChatVisibleTab } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';
import { t } from '../../../localization/t';

// Spectators are strictly read-only. Only this allow-list of hotkeys
// reaches dispatchHotkeyAction. Anything that mutates game state
// (draws, taps, flips, life/mana, pile searches, pings, pass-turn,
// activate, group, etc.) is swallowed. Keep this list narrow; new
// read-only hotkeys must be added explicitly.
const SPECTATOR_ALLOWED_ACTIONS = new Set([
  'game.showHelp',
  'card.inspect',
  'board.expandSquare',
  'camera.panUp',
  'camera.panDown',
  'camera.panLeft',
  'camera.panRight',
  'camera.zoomOverview',
  'camera.zoomCard',
  'camera.flipPerspective',
  'view.surface',
  'view.subsurface',
  'game.tokenBrowser',
  'game.focusChat', // existing branch no-ops for spectators
  'cancel',
]);

export function handleGameHotkey(host, event) {
  // Don't fire hotkeys while the user is typing in a text field
  // (e.g. the pile search dialog's search input).
  const tag = event.target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
  // Native button activation belongs to the focused control. Enter must not
  // open chat and Space must not inspect a stale hovered card instead.
  if (tag === 'BUTTON' && (event.key === 'Enter' || event.key === ' ')) return;

  // Escape is intentionally non-remappable. It's the universal
  // "back out" key and every modal / submenu relies on it.
  if (event.key === 'Escape') {
    host.closeAllMenus();
    // Pending discard-cost pick (Ignis Rex) backs out first. The
    // activation was deferred, so cancelling charges nothing.
    if (host._cancelDiscardCostPick?.()) return;
    if (host.state.showShortcutHelp) {
      host.setState({ showShortcutHelp: false });
      return;
    }
    if (host.state.showExitConfirm) {
      host.setState({ showExitConfirm: false });
    } else {
      host.requestExit();
    }
    return;
  }

  // Mulligan-phase keybinds. Modal, intentionally non-remappable.
  const activeMulliganKeys = host._mulliganKeys(host.state.activePlayerNum);
  const mulliganStage = host.state[activeMulliganKeys.phase];
  if (mulliganStage === 'prompt') {
    if (event.key === 'y' || event.key === 'Y' || event.key === 'Enter') {
      event.preventDefault();
      host.acceptMulligan();
      return;
    }
    if (event.key === 'n' || event.key === 'N' || event.key === 'k' || event.key === 'K') {
      event.preventDefault();
      host.keepHand();
      return;
    }
  } else if (mulliganStage === 'selecting') {
    if (event.key === 'm' || event.key === 'M') {
      event.preventDefault();
      host.executeMulligan();
      return;
    }
    if (event.key === 'k' || event.key === 'K' || event.key === 'Escape') {
      event.preventDefault();
      host.keepHand();
      return;
    }
  }

  const actionId = matchAction(event);

  // The card inspector is modal: the ONLY key it listens for is the
  // inspect-action keybinding (Space by default) which closes the
  // inspector. All other gameboard hotkeys are suppressed until it closes.
  if (host.state.inspectedCard) {
    if (actionId === 'card.inspect') {
      event.preventDefault();
      // Swallow OS key-repeat while Space is held so hold-to-inspect
      // stays open. Keyup is the only event that should close it.
      if (event.repeat) return;
      host.inspectorHold.noteClose();
      host.setState({ inspectedCard: null, inspectorStack: [] });
      host.scene?.setPaused(false);
      host._presenceHover(host.hoveredMesh?.userData?.cardInstance?.id || null);
    }
    return;
  }

  if (host.state.showExitConfirm || host.state.showDeckPicker) return;

  if (actionId && host.dispatchHotkeyAction(actionId, event)) return;

  // Start-phase draw prompt: 1 → Atlas, 2 → Spellbook. Takes
  // precedence over the hovered-pile multi-draw binding below so a
  // player hovering a deck while the prompt is up can't accidentally
  // draw 1/2 cards from it. The mandatory draw resolves instead.
  // Mirrors the on-button keycap indicators in StartDrawOverlay.
  // Magician (`_singleDraw`) collapses both to the spellbook draw.
  const drawPrompt = host._localDrawPhaseChoicePrompt();
  if (drawPrompt && (event.key === '1' || event.key === '2')) {
    event.preventDefault();
    const choice = drawPrompt._singleDraw
      ? 'spellbook'
      : (event.key === '1' ? 'atlas' : 'spellbook');
    host.handlePromptConfirm(drawPrompt.id, choice);
    return;
  }

  // Number keys: draw N cards from the hovered pile. The authoritative
  // path requests the batch atomically and its hand fold staggers the
  // animations; the local fallback staggers each draw directly. Not
  // remappable. It's a range.
  // Spectators remain read-only; players may use this as the manual
  // tabletop override in both automated and manual matches.
  if (host.props.isSpectating) return;
  const drawCount = parseInt(event.key, 10);
  if (drawCount >= 1 && drawCount <= 9 && host.hoveredMesh?.userData?.type === 'pile') {
    const pileId = host.hoveredMesh.userData.pile.id;
    host.drawCardsStaggered(pileId, drawCount);
    return;
  }

  // Number keys on a hovered die: turn it to that face. Same range idea as
  // the pile draw, and a d4 ignores a 7.
  if (host.hoveredMesh?.userData?.type !== 'dice') return;
  const { diceInstance } = host.hoveredMesh.userData;
  const face = dieFaceForKey(diceInstance, event.key);
  if (face === null) return;
  event.preventDefault();
  host.setDiceValue(diceInstance, face);
}

/** The face a digit key asks of this die, or null when the die has no such face. */
export function dieFaceForKey(diceInstance, key) {
  if (!/^[1-9]$/.test(key)) return null;
  const face = Number(key);
  return face <= (DICE_CONFIGS[diceInstance?.dieType]?.faces || 6) ? face : null;
}

// Dispatch a matched action id to its handler. Returns true when the
// action was fully handled so the caller can early-return.
export function dispatchHotkeyAction(host, actionId, event) {
  // Spectator allow-list: only camera / inspect / view-toggle / help
  // hotkeys reach a handler. Everything else (draws, taps, flips,
  // life/mana, pile searches, pings, pass-turn, etc.) is swallowed
  // so a spectator can't mutate game state through the keyboard.
  if (host.props.isSpectating && !SPECTATOR_ALLOWED_ACTIONS.has(actionId)) {
    event?.preventDefault?.();
    return true;
  }
  switch (actionId) {
    case 'game.showHelp':
      host.setState((s) => ({ showShortcutHelp: !s.showShortcutHelp }));
      return true;
    case 'game.passTurn':
      event.preventDefault();
      // Pass-turn behaves identically in casual sandbox and multiplayer:
      // `passTurn` already handles the casual case (it flips
      // `activePlayerNum` alongside `currentTurn` so the perspective +
      // hand swap follow naturally). Splitting Tab into a perspective-
      // only `toggleActivePlayer` left the HUD's "whose turn"
      // highlight. Keyed off `currentTurn`. Stale until the user
      // hit End Turn.
      host.passTurn();
      return true;
    case 'card.inspect': {
      event.preventDefault();
      // OS key-repeat fires every ~40ms while Space is held. The
      // first keydown opens the inspector; subsequent repeats are
      // no-ops so the inspector stays open until keyup.
      if (event.repeat) return true;
      // A hovered unit-hand (owner-locked) card wins. It's a DOM overlay
      // with no mesh / hand index, so the checks below would miss it.
      // It renders ABOVE the prompt backdrop, so it stays live (and
      // clears on mouseleave) even while a blocking prompt is up.
      if (host._hoveredUnitSpell) {
        host.inspectorHold.noteOpen();
        host.setState({ inspectedCard: host._hoveredUnitSpell });
        host.scene?.setPaused(true);
        host._presenceHover(null);
        return true;
      }
      // `hoveredHandIndex` is the authoritative "cursor is in the hand
      // right now" signal. The hand DOM clears it on mouseleave. By
      // contrast `hoveredMesh` only updates via the 3D raycaster, so it
      // stays stale on the last board card after the cursor moves into
      // the hand overlay. Check the live hand index first. Like the
      // unit-hand fan, the hand overlay renders ABOVE the prompt
      // backdrop, so it stays live (and self-clears) even while a
      // blocking prompt is up and must keep winning over the prompt
      // branches below.
      const { hoveredHandIndex } = host.state;
      const activeHand = host.getActiveHandCards();
      if (hoveredHandIndex >= 0 && activeHand[hoveredHandIndex]) {
        host.inspectorHold.noteOpen();
        host.setState({ inspectedCard: activeHand[hoveredHandIndex] });
        host.scene?.setPaused(true);
        host._presenceHover(null);
        return true;
      }
      // A board-blocking storyline prompt (an opponent-cast [Accept]
      // acknowledge, an in-drawer choice) mounts a full-canvas backdrop,
      // so the canvas receives no mousemove and hoveredMesh stays
      // frozen on whatever board card was under the cursor before the
      // prompt opened. Don't resolve against it. With no live hover
      // above, the only card the player can be aiming at is the
      // prompt's own card (valkenhall-qa#80: Space during an [Accept]
      // window enlarged a stale board card).
      const blockingPrompt = boardBlockingPrompt(host.state.pendingPrompts, host.state.pendingServerChoice);
      if (blockingPrompt) {
        const promptCard = blockingPrompt.cardInstance;
        if (promptCard) {
          host.inspectorHold.noteOpen();
          host.setState({ inspectedCard: promptCard });
          host.scene?.setPaused(true);
          host._presenceHover(null);
        }
        return true;
      }
      // The server modal-choice drawer (pendingServerChoice: Witch's
      // Curse "choose one", scry) renders as a synthetic 'choice'
      // prompt, so it mounts the same backdrop and freezes hoveredMesh
      // the same way. cell / target picks render as board-pick prompts
      // and keep the board live, so only the modal kind blocks.
      const serverChoice = host.state.pendingServerChoice;
      if (serverChoice?.kind === 'modal') {
        const displayCard = serverChoiceDisplayCard(host, serverChoice);
        if (displayCard) {
          host.inspectorHold.noteOpen();
          host.setState({ inspectedCard: displayCard });
          host.scene?.setPaused(true);
          host._presenceHover(null);
        }
        return true;
      }
      if (host.hoveredMesh?.userData?.type === 'card') {
        const card = host.hoveredMesh.userData.cardInstance;
        host.inspectorHold.noteOpen();
        host.setState({ inspectedCard: card });
        host.scene?.setPaused(true);
        host._presenceHover(card.id);
        return true;
      }
      if (host.hoveredMesh?.userData?.type === 'opponentHandFace') {
        host.inspectorHold.noteOpen();
        host.openOpponentHandInspector(host.hoveredMesh.userData.cardInstance);
        return true;
      }
      // No card hovered. Arm so the next hover-enter opens.
      // tryOpenInspectorFromArmed() runs on every hover update.
      host.inspectorHold.noteArmed();
      return true;
    }
    case 'card.flip':
      host.hotkeyFlip();
      return true;
    case 'card.tap':
      host.hotkeyTap();
      return true;
    // hoveredMesh is frozen at the start of any drag (handleMouseMove's
    // drag branch returns before the hover update), so reading it during
    // a drag acts on a stale, possibly opponent-owned card. Skip the
    // destructive routes when a drag is in flight.
    case 'card.toHand':
      if (host.dragging || host.handDrag) return true;
      if (host.hoveredMesh?.userData?.type === 'card') {
        host.sendToHand(host.hoveredMesh.userData.cardInstance);
      }
      return true;
    case 'card.toCemetery':
      if (host.dragging || host.handDrag) return true;
      if (host.hoveredMesh?.userData?.type === 'card') {
        // Single cemetery sink. SendCardToPile owns the site→Rubble routing,
        // token banish-redirect, and Deathrite firing for EVERY send path
        // (hotkey, drag, context menu). Never branch on card type here.
        host.sendCardToPile(host.hoveredMesh.userData.cardInstance, 'Cemetery');
      } else if (host.hoveredMesh?.userData?.type === 'dice') {
        host.deleteDice(host.hoveredMesh.userData.diceInstance);
      }
      return true;
    case 'card.group':
      host.toggleGroupForSelection();
      event.preventDefault();
      return true;
    case 'ping.fire':
      event.preventDefault();
      if (event.repeat) return true;
      if (host.dragging || host.handDrag) return true;
      if (host.props.isSpectating) return true;
      if (!host.pingMode) {
        host.pingMode = true;
        host.setState({ pingMode: true });
      }
      return true;
    case 'board.expandSquare':
      // Held key (mirror of ping.fire): while down, square-selection mode.
      // the hover highlight shows and clicking an occupied square fans it
      // open. Released in handleWindowKeyUp / handleWindowBlur.
      event.preventDefault();
      if (event.repeat) return true;
      if (host.dragging || host.handDrag) return true;
      host.squareMode = true;
      return true;
    case 'grab':
      // Controller-native. Keyboard users never reach this branch
      // because `grab` has no keyboard binding.
      if (host.controllerGrabbedCard) host.controllerDropGrab();
      else host.controllerStartGrab();
      return true;
    case 'cancel':
      if (host.controllerGrabbedCard) host.controllerCancelGrab();
      return true;
    case 'camera.panUp':
      host.scene?.setKeyHeld('w', true);
      return true;
    case 'camera.panDown':
      host.scene?.setKeyHeld('s', true);
      return true;
    case 'camera.panLeft':
      host.scene?.setKeyHeld('a', true);
      return true;
    case 'camera.panRight':
      host.scene?.setKeyHeld('d', true);
      return true;
    case 'camera.zoomOverview':
      host.scene?.zoomToOverview();
      return true;
    case 'camera.zoomCard':
      if (host.hoveredMesh) {
        const pos = host.hoveredMesh.position;
        host.scene?.zoomToCard(pos.x, pos.z);
      }
      return true;
    case 'camera.flipPerspective':
      host.scene?.flipPerspective();
      return true;
    case 'pile.shuffle':
      if (host.hoveredMesh?.userData?.type === 'pile') {
        const pile = host.hoveredMesh.userData.pile;
        // Same ownership gate the pile context menu applies (pointerInteractions
        // drops the menu entirely for an opponent pile). It matters more here
        // than it reads: this hotkey now EMITS, so without the gate hovering the
        // opponent's deck and pressing the key would shuffle their real deck on
        // the server. Sandbox and offline bypass it, as everywhere else.
        if (!host.isPileLocallyOwned?.(pile)) return true;
        animateShufflePile(host.hoveredMesh, pile, host.scene.scene);
        playSound(pile.name === 'Atlas' ? 'cardShuffleAtlas' : 'cardShuffleSpellbook');
        shufflePile(host.state.gameState, pile.id);
        logShuffle(host, pile.name);
        // The local shuffle above is a PLACEHOLDER the server overwrites one
        // RTT later. Without this emit the deck was only ever shuffled in the
        // render and truth kept the pre-shuffle order (#53).
        host._emitShuffle?.(pile);
      } else if (host.hoveredMesh?.userData?.type === 'dice') {
        host.rollDice(host.hoveredMesh.userData.diceInstance);
      }
      return true;
    case 'pile.searchSpellbook':
    case 'pile.searchAtlas':
    case 'pile.searchCemetery':
    case 'pile.searchCollection':
    case 'pile.searchBanished': {
      event.preventDefault();
      // Opening Spellbook / Atlas is a backdoor draw (take-to-hand
      // from the search dialog), so it's gated by the same start-
      // phase prompt as the pile click. Cemetery / Collection /
      // Banished are public information and stay open.
      if (
        (actionId === 'pile.searchSpellbook' || actionId === 'pile.searchAtlas')
        && host._localDrawPhaseChoicePrompt()
      ) {
        toast(t('Use Draw a Spell or Draw a Site first'));
        return true;
      }
      let pile = null;
      let pileLabel = '';
      if (actionId === 'pile.searchCemetery') {
        pile = host.findOrCreateCemetery(host.getActivePlayerRotated());
        pileLabel = t('Cemetery');
      } else if (actionId === 'pile.searchBanished') {
        // Banished is public information with a tab-bar in the dialog,
        // so eagerly create BOTH players' piles to back the tabs.
        host.findOrCreateBanished(false);
        host.findOrCreateBanished(true);
        pile = host.findOrCreateBanished(host.getActivePlayerRotated());
        pileLabel = t('Banished');
      } else if (actionId === 'pile.searchAtlas') {
        pile = host.findLocalPileByName('Atlas');
        pileLabel = t('Atlas');
      } else if (actionId === 'pile.searchSpellbook') {
        pile = host.findLocalPileByName('Spellbook');
        pileLabel = t('Spellbook');
      } else {
        pile = host.findLocalPileByName('Collection');
        pileLabel = t('Collection');
      }
      if (pile) {
        if (host.state.searchPile && host.state.searchPile.id === pile.id) {
          host.handlePileSearchClose();
        } else {
          host.openPileSearch(pile);
        }
      } else {
        toast(t('No {pile} pile yet', { pile: pileLabel }));


      }
      return true;
    }
    case 'game.tokenBrowser':
      event.preventDefault();
      if (host.state.searchPile && host.state.searchPile.id === 'token-pile') {
        host.handlePileSearchClose();
      } else {
        host.openPileSearch(host.buildTokenPile());
      }
      return true;
    case 'view.surface':
      event.preventDefault();
      if (host.state.regionView !== 'surface') host.setState({ regionView: 'surface' });
      return true;
    case 'view.subsurface':
      event.preventDefault();
      if (host.state.regionView !== 'subsurface') host.setState({ regionView: 'subsurface' });
      return true;
    case 'game.focusChat': {
      // Opens / reveals the in-log chat input without touching the
      // mouse. Spectators don't have chat, so bail for them.
      if (host.props.isSpectating) return true;
      const focusInput = () => {
        const el = host.chatInputRef.current;
        if (el) el.focus();
      };
      // The chat input lives on any chat-showing tab. Reveal the log and
      // clear the unread badge before focusing, but KEEP a tab that already
      // shows chat. The header toggle collapses the panel without touching
      // the tab, so a player on 'both' can be collapsed-and-on-both; forcing
      // 'chat' there would hide the log they chose to keep on screen, every
      // time they pressed Enter.
      const tab = isChatVisibleTab(host.state.logbookTab) ? host.state.logbookTab : 'chat';
      if (!host.state.showGameLog || tab !== host.state.logbookTab) {
        host.setState({ showGameLog: true, logbookTab: tab, chatUnread: false }, () => setTimeout(focusInput, 0));
      } else {
        host.setState({ chatUnread: false }, focusInput);
      }
      event.preventDefault();
      return true;
    }
    case 'game.lifeUp':
    case 'game.lifeDown':
    case 'game.manaUp':
    case 'game.manaDown': {
      const localPlayer = host.state.isHost ? 'p1' : 'p2';
      const trackerKey = (actionId === 'game.lifeUp' || actionId === 'game.lifeDown') ? 'life' : 'mana';
      const up = actionId === 'game.lifeUp' || actionId === 'game.manaUp';
      playSound('uiClick');
      if (up) host.incrementTracker(localPlayer, trackerKey);
      else host.decrementTracker(localPlayer, trackerKey);
      event.preventDefault();
      return true;
    }
    default:
      return false;
  }
}

// Flip the selection as a unit, or the hovered table card, or the
// hovered hand card. In that priority order. When a selection is
// flipped every card ends up on the SAME target face (matches the
// user's mental model of "flip the group"). Hand flips stay local
// because the opponent never sees individual hand cards.
export function hotkeyFlip(host) {
  if (host.selectedCardIds.size > 0) {
    const meshes = [];
    for (const id of host.selectedCardIds) {
      const mesh = host.meshes.get(id);
      if (mesh?.userData?.type === 'card') meshes.push(mesh);
    }
    if (meshes.length === 0) return;
    const flippable = meshes.filter((m) => host._canFlipByHotkey(m.userData.cardInstance));
    if (flippable.length === 0) {
      host._toastFlipLocked(meshes[0].userData.cardInstance);
      return;
    }
    const targetFaceDown = !flippable.some((m) => m.userData.cardInstance.faceDown);
    let flipped = 0;
    for (const mesh of flippable) {
      const card = mesh.userData.cardInstance;
      if (card.faceDown === targetFaceDown) continue;
      card.faceDown = targetFaceDown;
      syncCardFace(card);
      animateCardFlip(mesh, card);
      host._broadcastFlip(card, card.faceDown);
      flipped++;
    }
    if (flipped > 0) {
      if (targetFaceDown) {
        if (flipped > 1) host.logAction('Flipped {n} cards face down', { n: flipped });
        else host.logAction('Flipped 1 card face down');
      } else if (flipped > 1) host.logAction('Flipped {n} cards face up', { n: flipped });
      else host.logAction('Flipped 1 card face up');
    }
    playSound('cardFlip');
    return;
  }
  // Skip the hovered-mesh fallback during a drag. HoveredMesh isn't
  // refreshed mid-drag and would point at a stale (possibly opponent)
  // card. The selection branch above is unaffected and still fires.
  if (!host.dragging && !host.handDrag && host.hoveredMesh?.userData?.type === 'card') {
    const card = host.hoveredMesh.userData.cardInstance;
    if (!host._canFlipByHotkey(card)) {
      host._toastFlipLocked(card);
      return;
    }
    card.faceDown = !card.faceDown;
    syncCardFace(card);
    animateCardFlip(host.hoveredMesh, card);
    playSound('cardFlip');
    host._broadcastFlip(card, card.faceDown);
    const isTwoSided = Boolean(card.backFaceImagePath || card.backFaceImageUrl);
    // Flipping UP reveals the card (log names it from here on); flipping
    // DOWN hides it (name withheld). cardLogLabel reads the just-toggled
    // faceDown. Two-sided cards are always named. Both faces are public.
    const label = { card: cardLogLabel(card) };
    if (isTwoSided) {
      if (card.faceDown) host.logAction('Flipped {card} to back face', label);
      else host.logAction('Flipped {card} to front face', label);
    } else if (card.faceDown) host.logAction('Flipped {card} face down', label);
    else host.logAction('Flipped {card} face up', label);
    return;
  }
  const hhi = host.state.hoveredHandIndex;
  const activeHand = host.getActiveHandCards();
  if (hhi >= 0 && activeHand[hhi]) {
    const targetCard = activeHand[hhi];
    const handKey = host._handStateKeyForCard(targetCard);
    host.setState((state) => ({
      [handKey]: (state[handKey] || []).map((c) =>
        c.id === targetCard.id ? { ...c, faceDown: !c.faceDown } : c,
      ),
    }));
    playSound('cardFlip');
  }
}

// Tap / untap selection or hovered card.
export function hotkeyTap(host) {
  if (host.selectedCardIds.size > 0) {
    const meshes = [];
    for (const id of host.selectedCardIds) {
      const mesh = host.meshes.get(id);
      if (mesh?.userData?.type === 'card') {
        meshes.push(mesh);
      }
    }
    if (meshes.length === 0) return;
    const targetTapped = !meshes.every((m) => m.userData.cardInstance.tapped);
    let tapped = 0;
    for (const mesh of meshes) {
      if (host._applyTap(mesh.userData.cardInstance, mesh, targetTapped, {
        source: 'hotkey', log: false,
      })) tapped++;
    }
    if (tapped > 0) {
      if (targetTapped) {
        if (tapped > 1) host.logAction('Tapped {n} cards', { n: tapped });
        else host.logAction('Tapped 1 card');
      } else if (tapped > 1) host.logAction('Untapped {n} cards', { n: tapped });
      else host.logAction('Untapped 1 card');
    }
    playSound('cardPlace');
    return;
  }
  // Skip the hovered-mesh fallback during a drag. HoveredMesh isn't
  // refreshed mid-drag and would point at a stale (possibly opponent)
  // card. The selection branch above is unaffected and still fires.
  if (!host.dragging && !host.handDrag && host.hoveredMesh?.userData?.type === 'card') {
    const card = host.hoveredMesh.userData.cardInstance;
    const next = nextTapToggle(card);
    if (next !== null) {
      host._applyTap(card, host.hoveredMesh, next, { source: 'hotkey', sound: true });
    }
  }
}

// Draws `count` cards from a pile. Authoritative draw piles receive one
// counted server command; the non-authoritative fallback draws one-by-one
// with a small delay so the animation still feels fanned out.
export function drawCardsStaggered(host, pileId, count) {
  const pile = host.state.gameState.piles.find((p) => p.id === pileId);
  const pileName = pile?.name || msg('pile');
  if (count > 1) host.logAction('Drew {n} cards from {pile}', { n: count, pile: pileName });
  else host.logAction('Drew 1 card from {pile}', { pile: pileName });

  // In an authoritative match the server already accepts a counted DRAW and
  // the hand-additions fold staggers the resulting animations. Sending one
  // MOVE_TO_ZONE per timer tick lets live diffs interleave with the remaining
  // ticks, so the rendered deck can change underneath the batch. Keep the
  // whole draw atomic at the source of truth instead.
  const authZone = pile && host._authZonesEnabled?.()
    ? host._authZoneForPileName?.(pile.name)
    : null;
  if (authZone === 'spellbook' || authZone === 'atlas') {
    host._emitDraw(pile.rotated ? 'p2' : 'p1', authZone, count);
    return;
  }

  const STAGGER_MS = 140;
  for (let i = 0; i < count; i++) {
    if (i === 0) {
      // Skip per-card sync. We'll sync once after the last draw
      host.drawCard(pileId, { skipLog: true, skipSync: true });
    } else {
      const isLast = i === count - 1;
      setTimeout(() => {
        host.drawCard(pileId, { skipLog: true, skipSync: !isLast });
      }, i * STAGGER_MS);
    }
  }
}
